import { Box, ThemeProvider } from '@mui/material';
import { AdviceBox as AdviceBoxUI } from '@portals/core/src/components/AdviceBox/AdviceBox';
import { Button } from '@portals/core/src/components/Button/Button';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import type { Theme } from '@portals/core/src/themes/themes';
import { slugify } from '@portals/core/src/utilities/slugify';
import React, { ReactElement, useMemo } from 'react';

import { AdviceBox as AdviceBoxModel, InternText } from '../../../models';
import { Node } from '../../consumers/Consumer/Consumer';
import { useLinkProps } from '../../hooks/useLinkProps';
import { loadIcon } from '../../utils/loadIcon';

export interface Props {
  node: AdviceBoxModel;
  renderNode: (node: Node) => ReactElement;

  createTheme: () => Theme;
}

export const AdviceBox: React.FC<Props> = ({ node, renderNode, createTheme }: Props) => {
  const link = node.link && node.link.link ? node.link.link[0] : undefined;
  const { singleProps } = useLinkProps(link);
  const theme = useMemo(createTheme, [createTheme]);

  const Icon = loadIcon(node?.icon?.icon || 'Pin');

  return (
    <AdviceBoxUI variant="info" icon={<Icon fontSize="inherit" />}>
      <ThemeProvider theme={theme}>
        {node.overline && (
          <Typography sx={{ mb: 0 }} variant="h6" component="div">
            {node.overline.text}
          </Typography>
        )}
        {node.headline && (
          <Typography
            id={slugify(node.headline.text)}
            variant="body1"
            fontWeight="bold"
            component={node.headline.component}
          >
            {node.headline.text}
          </Typography>
        )}
        {node.text &&
          renderNode({
            __component: 'intern.text',
            id: `text.${node.id}`,
            text: node.text,
            variant: 'body1',
            innerPadding: 'none',
          } as InternText)}
        {link && (
          <Box alignSelf={{ xs: 'stretch', lg: 'flex-start' }}>
            <Button variant="standard" color="secondary" {...singleProps} fullWidth={true}>
              {link.text}
            </Button>
          </Box>
        )}
      </ThemeProvider>
    </AdviceBoxUI>
  );
};
